import React from 'react';
import { Home, DollarSign, Star, Landmark } from 'lucide-react';

const bancosOptions = [
  'BCP', 'BBVA', 'Interbank', 'Scotiabank', 'Banco de la Nación',
  'Banco Pichincha', 'Banco GNB', 'Banco Falabella', 'Mi Banco', 'Otro'
];

const amenidadesOptions = [
  'Piscina', 'Gimnasio', 'Áreas Verdes', 'Sala Común', 'Terraza', 
  'Estacionamiento', 'Seguridad 24/7', 'Juegos Infantiles', 'Zonas de BBQ',
  'SUM', 'Coworking', 'Lavandería', 'Ascensor'
];

const CarProyPublish = ({ 
  formData = {}, 
  handleInputChange, 
  setFormData,
  errors = {} 
}) => {
  const safeFormData = {
    tiene_1dorm: formData?.tiene_1dorm || false,
    tiene_2dorm: formData?.tiene_2dorm || false,
    tiene_3dorm: formData?.tiene_3dorm || false,
    tiene_4dorm: formData?.tiene_4dorm || false,
    tiene_financiamiento: formData?.tiene_financiamiento || 'No',
    bancos_financiamiento: formData?.bancos_financiamiento || [],
    amenidades: formData?.amenidades || [],
    otro_banco: formData?.otro_banco || '',
    detalles_financiamiento: formData?.detalles_financiamiento || '',
    otras_caracteristicas: formData?.otras_caracteristicas || '',
    ...formData
  };

  const handleUnitToggle = (dormCount, checked) => {
    setFormData(prev => ({
      ...prev,
      [`tiene_${dormCount}dorm`]: checked,
      // Limpiar datos si se desmarca
      ...(checked ? {} : {
        [`unidades_${dormCount}dorm`]: '',
        [`area_${dormCount}dorm`]: '',
        [`precio_${dormCount}dorm_soles`]: '',
        [`precio_${dormCount}dorm_dolares`]: ''
      })
    }));
  };

  const handlePriceChange = (e, dormCount) => {
    const solesValue = parseFloat(e.target.value);
    handleInputChange(e);
    
    // Auto-conversión a dólares
    if (!isNaN(solesValue) && solesValue > 0) {
      const dolaresValue = Math.round(solesValue / 3.80);
      setFormData(prev => ({
        ...prev,
        [`precio_${dormCount}dorm_dolares`]: dolaresValue.toString()
      }));
    }
  };

  const toggleArrayItem = (array, item) => {
    return array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <Home className="w-16 h-16 mx-auto text-purple-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Características del Proyecto
        </h2>
        <p className="text-gray-600">
          Define los tipos de unidades, precios y amenidades disponibles
        </p>
      </div>

      {/* Tipos de Unidades */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Home className="h-5 w-5 text-purple-500" />
          Tipos de Unidades Disponibles
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(num => (
            <div key={num} className="border-2 border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-colors">
              {/* Header de la unidad */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Home className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {num} Dormitorio{num > 1 ? 's' : ''}
                    </h4>
                    <p className="text-sm text-gray-500">Unidades de {num} dormitorio{num > 1 ? 's' : ''}</p>
                  </div>
                </div>
                
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={safeFormData[`tiene_${num}dorm`]}
                    onChange={(e) => handleUnitToggle(num, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">Disponible</span>
                </label>
              </div>
              
              {/* Detalles de la unidad */}
              {safeFormData[`tiene_${num}dorm`] && (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Cantidad */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        Cantidad de Unidades*
                      </label>
                      <input
                        type="number"
                        name={`unidades_${num}dorm`}
                        value={safeFormData[`unidades_${num}dorm`] || ''}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="0"
                        min="1"
                      />
                    </div>
                    
                    {/* Área */}
                    <div>
                      <label className="block text-sm font-medium mb-1 text-gray-700">
                        Área (m²)*
                      </label>
                      <input
                        type="number"
                        name={`area_${num}dorm`}
                        value={safeFormData[`area_${num}dorm`] || ''}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="0"
                        min="1"
                      />
                    </div>
                  </div>
                  
                  {/* Precio */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Precio desde (S/)*
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        name={`precio_${num}dorm_soles`}
                        value={safeFormData[`precio_${num}dorm_soles`] || ''}
                        onChange={(e) => handlePriceChange(e, num)}
                        className="w-full pl-10 p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    {safeFormData[`precio_${num}dorm_dolares`] && (
                      <p className="text-xs text-gray-500 mt-1">
                        ≈ US$ {safeFormData[`precio_${num}dorm_dolares`]}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Financiamiento */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Landmark className="h-5 w-5 text-emerald-500" />
          Opciones de Financiamiento
        </h3>
        
        {/* Toggle Financiamiento */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-3 text-gray-700">
            ¿El proyecto ofrece financiamiento bancario?
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tiene_financiamiento: "Si" }))}
              className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                safeFormData.tiene_financiamiento === "Si"
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 hover:border-emerald-300 text-gray-600'
              }`}
            >
              <Landmark className="h-6 w-6" />
              <div className="text-left">
                <div className="font-medium">Con Financiamiento</div>
                <div className="text-sm opacity-75">Bancos aliados disponibles</div>
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                tiene_financiamiento: "No",
                bancos_financiamiento: [],
                otro_banco: '',
                detalles_financiamiento: ''
              }))}
              className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                safeFormData.tiene_financiamiento === "No"
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 hover:border-emerald-300 text-gray-600'
              }`}
            >
              <div className="relative">
                <Landmark className="h-6 w-6" />
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                  <div className="w-8 h-0.5 bg-current transform rotate-45"></div>
                </div>
              </div>
              <div className="text-left">
                <div className="font-medium">Sin Financiamiento</div>
                <div className="text-sm opacity-75">Solo pago directo</div>
              </div>
            </button>
          </div>
        </div>
        
        {/* Bancos (si tiene financiamiento) */}
        {safeFormData.tiene_financiamiento === "Si" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-3 text-gray-700">
                Seleccione los bancos que financian este proyecto
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {bancosOptions.map((banco) => (
                  <button
                    key={banco}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        bancos_financiamiento: toggleArrayItem(prev.bancos_financiamiento || [], banco)
                      }));
                    }}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      safeFormData.bancos_financiamiento.includes(banco)
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-gray-200 hover:border-emerald-300 text-gray-600'
                    }`}
                  >
                    <span className="text-sm font-medium">{banco}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Otro banco */}
            {safeFormData.bancos_financiamiento?.includes('Otro') && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Especifique otro banco
                </label>
                <input
                  type="text"
                  name="otro_banco"
                  value={safeFormData.otro_banco}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Nombre del banco"
                />
              </div>
            )}
            
            {/* Detalles del financiamiento */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                Detalles del financiamiento (opcional)
              </label>
              <textarea
                name="detalles_financiamiento"
                value={safeFormData.detalles_financiamiento}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-vertical"
                placeholder="Ej: Cuota inicial desde 20%, financiamiento hasta 20 años, tasas preferenciales para trabajadores del estado..."
                rows="3"
              />
            </div>
          </div>
        )}
      </div>

      {/* Amenidades */}
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500" />
          Amenidades y Características Especiales
        </h3>
        
        <div className="space-y-4">
          {/* Grid de amenidades */}
          <div>
            <label className="block text-sm font-medium mb-3 text-gray-700">
              Seleccione las amenidades disponibles
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {amenidadesOptions.map((amenidad) => (
                <button
                  key={amenidad}
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      amenidades: toggleArrayItem(prev.amenidades || [], amenidad)
                    }));
                  }}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                    safeFormData.amenidades.includes(amenidad)
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                      : 'border-gray-200 hover:border-yellow-300 text-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    safeFormData.amenidades.includes(amenidad) 
                      ? 'bg-yellow-500 border-yellow-500' 
                      : 'border-gray-300'
                  }`}>
                    {safeFormData.amenidades.includes(amenidad) && (
                      <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium">{amenidad}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Otras características */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Otras características especiales (opcional)
            </label>
            <textarea
              name="otras_caracteristicas"
              value={safeFormData.otras_caracteristicas}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-vertical"
              placeholder="Mencione otras características especiales del proyecto como vista panorámica, tecnología smart home, certificaciones ambientales, etc."
              rows="3"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarProyPublish;